import { useEffect, useState, useCallback, useMemo, useRef, useContext } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Card,
  Row,
  Col,
  Descriptions,
  Table,
  InputNumber,
  DatePicker,
  Radio,
  Button,
  Modal,
  Statistic,
  Divider,
  Empty,
  Spin,
  Tag,
  Space,
  Typography,
  Alert,
  Input,
} from "antd";
import {
  FileExcelOutlined,
  EyeOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  DollarCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import "./Preview.css"

const { Title, Text } = Typography;
const { TextArea } = Input;

const FOOTER_OPTIONS = [
  { value: "SET_1", label: "GM HR / ACCTS EXE / VP(FINANCE) / AY / BY / MD" },
  { value: "SET_2", label: "GM HR / ACCTS EXE / VP(FINANCE) / BY" },
  { value: "SET_3", label: "GM HR / ACCTS EXE / VP(FINANCE) / BY / MD" },
];

const APPROVAL_TAG = {
  PENDING: { color: "gold", label: "Pending Approval" },
  APPROVED: { color: "success", label: "Approved" },
  REJECTED: { color: "error", label: "Rejected" },
};

const money = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;

const fmtDate = (v) => (v ? dayjs(v).format("DD MMM YYYY") : "—");
const fmtDateTime = (v) => (v ? dayjs(v).format("DD MMM YYYY, hh:mm A") : "—");

const inrFormatter = (v) => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const inrParser = (v) => v.replace(/₹\s?|,/g, "");

const sumAmounts = (rows) =>
  (rows || []).reduce((acc, r) => acc + Number(r.amount || 0), 0);

export default function FinalSettlementPanel({
  exitProcessId,
  authAccess,
  employee,
  messageApi,
  viewerIsSuperuser = false,
  onApprovalDecided,
}) {
  const notify =
    messageApi || {
      success: (m) => console.log("[success]", m),
      error: (m) => console.error("[error]", m),
      warning: (m) => console.warn("[warning]", m),
    };

  const [settlement, setSettlement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initiating, setInitiating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [calcRange, setCalcRange] = useState([null, null]);
  const [deciding, setDeciding] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");

  // Relieving Order & Service Certificate dispatch — only relevant once
  // the settlement approval is APPROVED (see the trigger card below).
  const [relievingLetter, setRelievingLetter] = useState(null);
  const [loadingRelievingLetter, setLoadingRelievingLetter] = useState(false);
  const [sendingRelievingLetter, setSendingRelievingLetter] = useState(false);

  // Whether the user has manually typed into the Net Payment box this
  // session — if so, we stop auto-recalculating it from the row totals.
  const [netOverridden, setNetOverridden] = useState(false);

  // Tracks the last value of monthly_gross_salary we know is persisted on
  // the server, so we can tell if the user has an unsaved edit pending.
  const lastSyncedGrossSalary = useRef(null);

  const { authData, backUrl } = useContext(AuthContext);

  // Single source of truth for "is this viewer a superuser" — combines
  // the explicit prop (used when opening from the approval queue) with
  // the logged-in user's own account flag, so direct navigation to this
  // panel by a superuser still renders the read-only / approval UI.
  const isSuperuser = Boolean(viewerIsSuperuser || authData?.employee?.is_superuser);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${authAccess}` }),
    [authAccess]
  );

  // -------------------------------------------------------------------
  // Live totals — derived from whatever is currently in state, so edits
  // to any row are reflected immediately without waiting on a save.
  // -------------------------------------------------------------------
  const liveCalcTotal = useMemo(
    () => sumAmounts(settlement?.calculation_rows),
    [settlement?.calculation_rows]
  );
  const liveArrearsTotal = useMemo(
    () => sumAmounts(settlement?.arrears),
    [settlement?.arrears]
  );
  const liveDeductionsTotal = useMemo(
    () => sumAmounts(settlement?.deductions),
    [settlement?.deductions]
  );
  const liveTotalPayable = liveCalcTotal + liveArrearsTotal;
  const liveNetPayment = liveTotalPayable - liveDeductionsTotal;

  // Keep Net Payment in sync with the row totals as long as the user
  // hasn't manually overridden it.
  useEffect(() => {
    if (!settlement || netOverridden) return;
    if (settlement.net_payment !== liveNetPayment) {
      setSettlement((p) => (p ? { ...p, net_payment: liveNetPayment } : p));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveNetPayment, netOverridden]);

  const load = useCallback(async () => {
    // if (!exitProcessId || !authAccess || !backUrl) {
    //   setLoading(false);
    //   return;
    // }
    setLoading(true);
    try {
      const res = await axios.get(
        `${backUrl}/api/exitprocess/${exitProcessId}/final-settlement/`,
        { headers: authHeaders }
      );
      setSettlement(res.data);
      lastSyncedGrossSalary.current = res.data.monthly_gross_salary;
      setNetOverridden(false);
      setCalcRange([
        res.data.calc_from_date ? dayjs(res.data.calc_from_date) : null,
        res.data.calc_to_date ? dayjs(res.data.calc_to_date) : null,
      ]);
    } catch (err) {
      if (err.response?.status !== 404) {
        notify.error(err.response?.data?.detail || "Could not load Full & Final settlement.");
      }
      setSettlement(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exitProcessId, authAccess, backUrl]);

  useEffect(() => {
    load();
  }, [load]);

  // -------------------------------------------------------------------
  // Relieving Order & Service Certificate — status + trigger.
  // Only meaningful once the settlement approval is APPROVED, so we only
  // bother fetching status at that point (and again right after a send).
  // -------------------------------------------------------------------
  const loadRelievingLetterStatus = useCallback(async () => {
    // if (!exitProcessId || !authAccess || !backUrl) return;
    setLoadingRelievingLetter(true);
    try {
      const res = await axios.get(
        `${backUrl}/api/exitprocess/${exitProcessId}/final-settlement/relieving-letter/`,
        { headers: authHeaders }
      );
      setRelievingLetter(res.data);
    } catch (err) {
      // Non-fatal — the trigger card just renders without prior status.
    } finally {
      setLoadingRelievingLetter(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exitProcessId, authAccess, backUrl]);

  useEffect(() => {
    if (settlement?.approval?.status === "APPROVED") {
      loadRelievingLetterStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlement?.approval?.status]);

  const handleSendRelievingLetter = async () => {
    setSendingRelievingLetter(true);
    try {
      const res = await axios.post(
        `${backUrl}/api/exitprocess/${exitProcessId}/final-settlement/relieving-letter/`,
        {},
        { headers: authHeaders }
      );
      setRelievingLetter(res.data);
      notify.success(`Relieving order & service certificate sent to ${res.data.sent_to_email}.`);
    } catch (err) {
      notify.error(
        err.response?.data?.detail ||
        "Could not send the relieving order & service certificate."
      );
    } finally {
      setSendingRelievingLetter(false);
    }
  };

  const handleInitiate = async () => {
    setInitiating(true);
    try {
      const res = await axios.post(
        `${backUrl}/api/exitprocess/${exitProcessId}/final-settlement/`,
        {},
        { headers: authHeaders }
      );
      setSettlement(res.data);
      lastSyncedGrossSalary.current = res.data.monthly_gross_salary;
      setNetOverridden(false);
      // notify.success("Full & Final settlement initiated and sent for approval.");
    } catch (err) {
      notify.error(
        err.response?.data?.detail ||
        "Last working day is not set for this employee. Set it before creating the Full & Final settlement."
      );
    } finally {
      setInitiating(false);
    }
  };


  if (isSuperuser) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      handleInitiate()
    }, [])
  }

  // Persists just the gross salary edit (used before "Generate" so the
  // calculation sheet is never built off a stale, unsaved figure).
  const persistGrossSalaryIfChanged = async () => {
    if (settlement.monthly_gross_salary === lastSyncedGrossSalary.current) return;
    const res = await axios.patch(
      `${backUrl}/api/exitprocess/${exitProcessId}/final-settlement/`,
      { monthly_gross_salary: settlement.monthly_gross_salary },
      { headers: authHeaders }
    );
    setSettlement(res.data);
    lastSyncedGrossSalary.current = res.data.monthly_gross_salary;
  };

  const handleGenerateCalcSheet = async () => {
    const [from, to] = calcRange;
    if (!from || !to) {
      notify.warning("Pick both From and To dates for the calculation sheet.");
      return;
    }
    setGenerating(true);
    try {
      await persistGrossSalaryIfChanged();
      const res = await axios.post(
        `${backUrl}/api/exitprocess/${exitProcessId}/final-settlement/calc-period/`,
        {
          calc_from_date: from.format("YYYY-MM-DD"),
          calc_to_date: to.format("YYYY-MM-DD"),
        },
        { headers: authHeaders }
      );
      setSettlement(res.data);
      lastSyncedGrossSalary.current = res.data.monthly_gross_salary;
      setNetOverridden(false);
      notify.success("Calculation sheet generated.");
    } catch (err) {
      notify.error(err.response?.data?.detail || "Could not generate the calculation sheet.");
    } finally {
      setGenerating(false);
    }
  };

  const updateLocalRow = (section, id, value) => {
    setSettlement((prev) => ({
      ...prev,
      [section]: prev[section].map((row) =>
        row.id === id ? { ...row, amount: value ?? 0 } : row
      ),
    }));
  };

  const handleNetPaymentChange = (val) => {
    setNetOverridden(true);
    setSettlement((p) => ({ ...p, net_payment: val ?? 0 }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        calculation_rows: settlement.calculation_rows.map((r) => ({ id: r.id, amount: r.amount })),
        arrears: settlement.arrears.map((r) => ({ id: r.id, amount: r.amount })),
        deductions: settlement.deductions.map((r) => ({ id: r.id, amount: r.amount })),
        footer_set: settlement.footer_set,
        monthly_gross_salary: settlement.monthly_gross_salary,
        // Only send net_payment as an explicit override if the user actually
        // typed into that box — otherwise let the backend derive it from the
        // row totals it just saved, so it can never drift from the rows.
        ...(netOverridden ? { net_payment: settlement.net_payment } : {}),
      };
      await axios.patch(
        `${backUrl}/api/exitprocess/${exitProcessId}/final-settlement/`,
        payload,
        { headers: authHeaders }
      );
      notify.success("Full & Final settlement saved.");
      // Re-fetch so the panel reflects exactly what's persisted, including
      // any calculation-sheet rows the backend repriced off the new salary,
      // the recomputed totals / net payment, and the (possibly reset)
      // approval status.
      await load();
    } catch (err) {
      notify.error(err.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    // if (!backUrl) {
    //   notify.error("Backend URL is not configured.");
    //   return;
    // }
    setDownloading(true);
    try {
      const res = await axios.get(
        `${backUrl}/api/exitprocess/${exitProcessId}/final-settlement/excel/`,
        { headers: authHeaders, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `FnF_${settlement.emp_code}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notify.error("Could not download the Excel file.");
    } finally {
      setDownloading(false);
    }
  };

  const submitDecision = async (action, remarks) => {
    setDeciding(true);
    try {
      const res = await axios.post(
        `${backUrl}/api/exitprocess/${exitProcessId}/final-settlement/approval/action/`,
        { action, remarks },
        { headers: authHeaders }
      );
      setSettlement(res.data);
      notify.success(
        action === "APPROVE"
          ? "Settlement approved. Employee status updated to Resigned."
          : "Settlement rejected."
      );
      onApprovalDecided?.(res.data);
    } catch (err) {
      notify.error(err.response?.data?.detail || "Could not record the decision.");
    } finally {
      setDeciding(false);
    }
  };

  const handleApprove = () => submitDecision("APPROVE", undefined);

  const handleRejectConfirm = async () => {
    if (!rejectRemarks.trim()) {
      notify.warning("Remarks are required when rejecting.");
      return;
    }
    await submitDecision("REJECT", rejectRemarks.trim());
    setRejectModalOpen(false);
    setRejectRemarks("");
  };

  // -------------------------------------------------------------------
  // Empty / loading states
  // -------------------------------------------------------------------

  if (!exitProcessId) {
    return (
      <Card className="ffs-card" bordered>
        <Empty description="Assign approvers first to start a Full & Final settlement." />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="ffs-card" bordered style={{ textAlign: "center", padding: 40 }}>
        <Spin tip="Loading settlement…" />
      </Card>
    );
  }

  if (!settlement) {
    return (
      <Card className="ffs-card" bordered>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        // description="Full & Final settlement hasn't been initiated for this employee yet."
        >

          <Button
            type="primary"
            icon={<DollarCircleOutlined />}
            loading={initiating}
            onClick={handleInitiate}
          >
            View Full & Final Settlement
          </Button>
        </Empty>
      </Card>
    );
  }

  // -------------------------------------------------------------------
  // Table column defs (shared shape for arrears / deductions)
  // -------------------------------------------------------------------

  const editableAmountColumns = (section) => [
    {
      title: "Particulars",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 180,
      align: "right",
      render: (value, row) => (
        <InputNumber
          value={value}
          min={0}
          precision={2}
          style={{ width: "100%" }}
          formatter={inrFormatter}
          parser={inrParser}
          disabled={isSuperuser}
          onChange={(val) => updateLocalRow(section, row.id, val)}
        />
      ),
    },
  ];

  const calcSheetColumns = [
    { title: "Period", dataIndex: "label", key: "label" },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 180,
      align: "right",
      render: (value, row) => (
        <InputNumber
          value={value}
          min={0}
          precision={2}
          style={{ width: "100%" }}
          formatter={inrFormatter}
          parser={inrParser}
          disabled={isSuperuser}
          onChange={(val) => updateLocalRow("calculation_rows", row.id, val)}
        />
      ),
    },
  ];

  const grossSalaryDirty = settlement.monthly_gross_salary !== lastSyncedGrossSalary.current;
  const approval = settlement.approval;
  const approvalTag = approval ? APPROVAL_TAG[approval.status] : null;
  const canDecide = isSuperuser && approval?.status === "PENDING";

  return (
    <div className="ffs-wrapper">
      <div className="ffs-header-bar">
        <div>
          <Text className="ffs-eyebrow">Exit Clearance</Text>
          <Title level={3} style={{ margin: 0 }}>
            Full & Final Settlement
          </Title>
        </div>
        <Space>
          {/* <Tag color={settlement.status === "FINALIZED" ? "success" : "processing"}>
            {settlement.status === "FINALIZED" ? "Finalized" : "Draft"}
          </Tag> */}
          {/* {approvalTag && (
            <Tag color={approvalTag.color} style={{ fontWeight: 600 }}>
              {approvalTag.label}
            </Tag>
          )} */}
        </Space>
      </div>

      {/* Approval status card */}
      {approval && (
        <Card className="ffs-card" bordered style={{ borderColor: approval.status === "REJECTED" ? "#ffccc7" : undefined }}>
          <Row gutter={[16, 8]} align="middle">
            <Col xs={24} md={16}>
              <Space direction="vertical" size={2}>
                <Text strong>
                  Approval status: <Tag color={approvalTag.color}>{approvalTag.label}</Tag>
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Submitted by {approval.submitted_by ? `${approval.submitted_by.first_name || ""} ${approval.submitted_by.last_name || ""}`.trim() : "—"} on {fmtDateTime(approval.submitted_at)}
                </Text>
                {approval.status !== "PENDING" && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {approval.status === "APPROVED" ? "Approved" : "Rejected"} by {approval.decided_by ? `${approval.decided_by.first_name || ""} ${approval.decided_by.last_name || ""}`.trim() : "—"} on {fmtDateTime(approval.decided_at)}
                  </Text>
                )}
                {approval.remarks && (
                  <Alert
                    style={{ marginTop: 6 }}
                    type={approval.status === "REJECTED" ? "error" : "info"}
                    showIcon
                    message={approval.remarks}
                  />
                )}
              </Space>
            </Col>
            {canDecide && (
              <Col xs={24} md={8} style={{ textAlign: "right" }}>
                <Space>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    loading={deciding}
                    onClick={() => setRejectModalOpen(true)}
                  >
                    Reject
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={deciding}
                    onClick={handleApprove}
                  >
                    Approve
                  </Button>
                </Space>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {/* Relieving Order & Service Certificate — only once F&F is APPROVED */}
      {approval?.status === "APPROVED" && (
        <Card className="ffs-card" bordered title="Relieving Order & Service Certificate">
          <Row gutter={[16, 8]} align="middle">
            <Col xs={24} md={16}>
              {relievingLetter?.is_sent ? (
                <Space direction="vertical" size={2}>
                  <Text strong>
                    <MailOutlined style={{ marginRight: 6 }} />
                    Sent to {relievingLetter.sent_to_email}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {fmtDateTime(relievingLetter.sent_at)}
                    {relievingLetter.sent_by ? ` by ${relievingLetter.sent_by}` : ""}
                  </Text>
                </Space>
              ) : (
                <Text type="secondary">
                  Not sent yet. This emails the employee a PDF of their Relieving
                  Order & Service Certificate, merged with their details.
                </Text>
              )}
              {relievingLetter?.last_error && (
                <Alert
                  style={{ marginTop: 8 }}
                  type="error"
                  showIcon
                  message={relievingLetter.last_error}
                />
              )}
            </Col>
            <Col xs={24} md={8} style={{ textAlign: "right" }}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={sendingRelievingLetter || loadingRelievingLetter}
                onClick={handleSendRelievingLetter}
              >
                {relievingLetter?.is_sent ? "Resend" : "Send to employee"}
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {/* Employee summary */}
      <Card className="ffs-card" bordered title="Employee Details">
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
          <Descriptions.Item label="Name">{settlement.employee_name || "—"}</Descriptions.Item>
          <Descriptions.Item label="Emp. Code">{settlement.emp_code || "—"}</Descriptions.Item>
          <Descriptions.Item label="Designation">{settlement.designation || "—"}</Descriptions.Item>
          <Descriptions.Item label="Department">{settlement.department || "—"}</Descriptions.Item>
          <Descriptions.Item label="Location">{settlement.location || "—"}</Descriptions.Item>
          <Descriptions.Item label="Site / Project">{settlement.site_project || "—"}</Descriptions.Item>
          <Descriptions.Item label="Date of Joining">{fmtDate(settlement.date_of_joining)}</Descriptions.Item>
          <Descriptions.Item label="Last Working Day">{fmtDate(settlement.last_working_day)}</Descriptions.Item>
          <Descriptions.Item label="Service Duration">
            <Tag color="blue">{settlement.service_duration || "—"}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Reason for Leaving" span={2}>
            {settlement.reason_for_leaving || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Monthly Gross Salary">
            <InputNumber
              value={settlement.monthly_gross_salary}
              min={0}
              precision={2}
              style={{ width: 180 }}
              formatter={inrFormatter}
              parser={inrParser}
              disabled={isSuperuser}
              onChange={(val) =>
                setSettlement((p) => ({ ...p, monthly_gross_salary: val ?? 0 }))
              }
            />
            {settlement.gross_salary_period && (
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                (as of {settlement.gross_salary_period})
              </Text>
            )}
            {grossSalaryDirty && (
              <Tag color="warning" style={{ marginLeft: 8 }}>
                Unsaved
              </Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
        {grossSalaryDirty && (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message="Save (or Generate) to recalculate the settlement using the new gross salary. Manually edited calculation rows won't be overwritten."
          />
        )}
      </Card>

      {/* Calculation sheet period + rows */}
      <Card className="ffs-card" bordered title="Calculation Sheet">
        {!isSuperuser && (
          <Space wrap size="middle" style={{ marginBottom: 16 }}>
            <div>
              <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                From date
              </Text>
              <DatePicker
                value={calcRange[0]}
                onChange={(d) => setCalcRange([d, calcRange[1]])}
                format="DD/MM/YYYY"
              />
            </div>
            <div>
              <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                To date
              </Text>
              <DatePicker
                value={calcRange[1]}
                onChange={(d) => setCalcRange([calcRange[0], d])}
                format="DD/MM/YYYY"
              />
            </div>
            <Button
              type="primary"
              ghost
              // icon={<ThunderboltOutlined />}
              loading={generating}
              onClick={handleGenerateCalcSheet}
              style={{ marginTop: 20 }}
            >
              Generate
            </Button>
          </Space>
        )}

        {settlement.calculation_rows.length > 0 ? (
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            columns={calcSheetColumns}
            dataSource={settlement.calculation_rows}
            components={{
              header: {
                cell: (props) => (
                  <th {...props} style={{ backgroundColor: "#0e497b", color: 'white', marginTop: '20px' }}>
                    {props.children}
                  </th>
                ),
              },
            }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Text strong>Calculation sheet total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong>{money(liveCalcTotal)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
            bordered
          />
        ) : (
          <Alert
            type="info"
            showIcon
            message="Pick a date range and click Generate to build the month-wise calculation rows automatically from the monthly gross salary."
          />
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            className="ffs-card"
            bordered
            title="Arrears if any"
            extra={<Text type="secondary">{isSuperuser ? "Read only" : "Editable"}</Text>}
          >
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              columns={editableAmountColumns("arrears")}
              dataSource={settlement.arrears}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Total amount payable</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>{money(liveTotalPayable)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            className="ffs-card"
            bordered
            title="Deductions"
            extra={<Text type="secondary">{isSuperuser ? "Read only" : "Editable"}</Text>}
          >
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              columns={editableAmountColumns("deductions")}
              dataSource={settlement.deductions}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Total Deductions</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>{money(liveDeductionsTotal)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* Net payment + footer selection */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={10}>
          <Card className="ffs-card ffs-net-card" bordered>
            <Statistic
              title="Net - Payment"
              value={settlement.net_payment}
              precision={2}
              prefix="₹"
              valueStyle={{ color: "#2f5d50", fontWeight: 700 }}
            />
            <Divider style={{ margin: "12px 0" }} />
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
              Auto-calculated as Total payable − Total deductions. Edit to override.
            </Text>
            <InputNumber
              value={settlement.net_payment}
              min={0}
              precision={2}
              style={{ width: "100%" }}
              formatter={inrFormatter}
              parser={inrParser}
              disabled={isSuperuser}
              onChange={handleNetPaymentChange}
            />
            {netOverridden && !isSuperuser && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, marginTop: 4 }}
                onClick={() => setNetOverridden(false)}
              >
                Reset to auto-calculated value
              </Button>
            )}
          </Card>
        </Col>

        <Col xs={24} md={14}>
          <Card className="ffs-card" bordered title="Footer Signatories">
            <Radio.Group
              value={settlement.footer_set}
              disabled={isSuperuser}
              onChange={(e) =>
                setSettlement((p) => ({ ...p, footer_set: e.target.value }))
              }
            >
              <Space direction="vertical">
                {FOOTER_OPTIONS.map((opt) => (
                  <Radio key={opt.value} value={opt.value}>
                    {opt.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Card>
        </Col>
      </Row>

      {/* Action bar */}
      <Card className="ffs-card ffs-actions-card" bordered>
        <Space wrap>
          {!isSuperuser && (
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
              Save
            </Button>
          )}
          <Button icon={<EyeOutlined />} onClick={() => setShowPreview(true)}>
            Preview
          </Button>
          <Button icon={<FileExcelOutlined />} loading={downloading} onClick={handleDownload}>
            Download Excel
          </Button>
        </Space>
      </Card>

      <PreviewModal
        open={showPreview}
        settlement={settlement}
        onClose={() => setShowPreview(false)}
      />

      <Modal
        open={rejectModalOpen}
        title="Reject Full & Final Settlement"
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectRemarks("");
        }}
        onOk={handleRejectConfirm}
        okText="Reject"
        okButtonProps={{ danger: true, loading: deciding }}
      >
        <Text type="secondary">Please provide a reason. This will be visible to HR.</Text>
        <TextArea
          style={{ marginTop: 8 }}
          rows={4}
          value={rejectRemarks}
          onChange={(e) => setRejectRemarks(e.target.value)}
          placeholder="Reason for rejection…"
        />
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview modal — read-only replica of the printed sheet
// ---------------------------------------------------------------------------

function PreviewModal({ open, settlement, onClose }) {
  if (!settlement) return null;

  const rows = [
    ["Name of the Employee", settlement.employee_name],
    ["Emp.Code", settlement.emp_code],
    ["Designation", settlement.designation],
    ["Department", settlement.department],
    ["Location", settlement.location],
    ["Site/Project", settlement.site_project],
    ["Date of Joining", fmtDate(settlement.date_of_joining)],
    ["Last working day", fmtDate(settlement.last_working_day)],
    ["Service Duration", settlement.service_duration],
    ["Reasons for Leaving", settlement.reason_for_leaving],
    [
      "Monthly Salary in Gross",
      settlement.gross_salary_period
        ? `${money(settlement.monthly_gross_salary)} (as of ${settlement.gross_salary_period})`
        : money(settlement.monthly_gross_salary),
    ],
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      title={null}
      className="ffs-preview-modal"
    >
      <div className="ffs-preview-doc">
        <div className="ffs-preview-title">
          {/* <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            BAASHYAAM CONSTRUCTIONS PVT LTD
          </Title> */}
          <Title level={5} style={{ margin: 0, textAlign: "center" }}>
            FULL AND FINAL SETTLEMENT
          </Title>
          <Text type="secondary" style={{ display: "block", textAlign: "center", marginTop: 4 }}>
            Date: {fmtDate(settlement.settlement_date)}
          </Text>
        </div>

        <table className="ffs-preview-table">
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td className="ffs-preview-label">{label}</td>
                <td className="ffs-preview-value">{value || "-"}</td>
              </tr>
            ))}

            <tr className="ffs-preview-section-row">
              <td>CALCULATION SHEET</td>
              <td className="ffs-preview-value">Amount</td>
            </tr>
            {settlement.calculation_rows.map((r) => (
              <tr key={r.id}>
                <td>{r.label}</td>
                <td className="ffs-preview-value">{money(r.amount)}</td>
              </tr>
            ))}

            <tr className="ffs-preview-section-row">
              <td>Arrears if any:</td>
              <td />
            </tr>
            {settlement.arrears.map((r) => (
              <tr key={r.id}>
                <td>{r.label}</td>
                <td className="ffs-preview-value">{money(r.amount)}</td>
              </tr>
            ))}

            <tr className="ffs-preview-total-row">
              <td>Total amount payable</td>
              <td className="ffs-preview-value">{money(settlement.total_amount_payable)}</td>
            </tr>

            <tr className="ffs-preview-section-row">
              <td>Deductions</td>
              <td />
            </tr>

            {settlement.deductions.map((r) => (
              <tr key={r.id}>
                <td>{r.label}</td>
                <td className="ffs-preview-value">{money(r.amount)}</td>
              </tr>
            ))}

            <tr className="ffs-preview-total-row">
              <td>Total Deductions</td>
              <td className="ffs-preview-value">{money(settlement.total_deductions)}</td>
            </tr>

            <tr className="ffs-preview-net-row">
              <td>Net - Payment</td>
              <td className="ffs-preview-value">{money(settlement.net_payment)}</td>
            </tr>
          </tbody>
        </table>

        <Text italic type="secondary" style={{ display: "block", marginTop: 16, fontSize: 12 }}>
          Declaration: The above mentioned all details are true and accepted from my knowledge.
        </Text>

        <div className="ffs-preview-footer">
          {settlement.footer_signatories.map((s) => (
            <span key={s}>{s}</span>
          ))}
        </div>
      </div>
    </Modal>
  );
}